import type { CrmObject } from '@/types/crm';

/**
 * The FE-facing names a memo surfaces under:
 *   - `task` — an `ahoy_task` record (task-ness is the entity type, never
 *              inferred from metadata — epic #13640);
 *   - `note` — a plain note.
 *
 * Centralizing this here keeps every surface — composer placeholders and
 * submit labels, cards, empty states, toasts — naming a memo consistently
 * instead of each re-deriving the rule.
 */
export type NoteKind = 'note' | 'task';

/**
 * Resolve the FE-facing kind for a NOTE. Notes have no stage/pipeline concept
 * and (as of #13884) no due date either, so this always returns 'note' — a
 * task is an `ahoy_task` record, which its surfaces already know without
 * asking.
 */
export function getNoteKind(): NoteKind {
  return 'note';
}

/**
 * Resolve a note's FE-facing kind straight from its CrmObject. Called only
 * for `ahoy_note` records (see useRecordActions). Used by every per-record
 * surface that only has the raw object (header, RecordBody) and by the
 * NoteRecordOverview dispatcher, so all three agree on one rule.
 */
export function getNoteKindFromObject(_object: CrmObject): NoteKind {
  return getNoteKind();
}

/**
 * True for a record that renders through the task surfaces (`record/task/*`,
 * the "Task" header chip, the Mark complete footer) — a real `ahoy_task`.
 */
export function isTaskRecord(object: CrmObject): boolean {
  return object.entity_type_name === 'ahoy_task';
}

/** Capitalized noun for headings/labels, e.g. `Note` / `Task`. */
export function noteKindLabel(kind: NoteKind): string {
  return kind.charAt(0).toUpperCase() + kind.slice(1);
}

/**
 * Titles that are placeholders, not content. Legacy notes were stamped with
 * one of these by a template default when the user gave no title, so every
 * surface must treat them as "no title" rather than render a heading that
 * just repeats the record's type.
 */
const PLACEHOLDER_TITLES = new Set(['note', 'new note', 'untitled note']);

/**
 * Whether a note-like record has a real, user-given title. Empty/whitespace
 * and the legacy placeholders above both count as "no title". One rule, one
 * home: NoteCard, TaskTitleField, CreateActionCard and the composer all read
 * this.
 */
export function hasMeaningfulTitle(title: string | null | undefined): boolean {
  const trimmed = title?.trim().toLowerCase() ?? '';
  return trimmed !== '' && !PLACEHOLDER_TITLES.has(trimmed);
}
